import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import { sortChatsByLastMessage } from 'utils/chats'
import { Icon } from 'components/ui/Icon'
import { UIButton } from 'components/ui/Button'
import { ChatRow } from 'components/chats/ChatRow'
import { ListEmptyComponent } from 'components/ui/ListEmptyComponent'
import { ChatBubbleSparklesSvg } from 'components/ui/svg/ChatBubbleSparklesSvg'
import { ChatPageSidebarFooter } from 'components/chat-page/ChatPageSidebarFooter'
import { messagesActions, messagesSelectors } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { useGetSetting } from 'api/settings'
import { useUserChatListSocket } from 'hooks/useUserChatListSocket'

type ChatPageSidebarProps = {
  onNavigate?: () => void
}

export const ChatPageSidebar = ({ onNavigate }: ChatPageSidebarProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { setting, title } = useGetSetting()
  const chatsMap = useSelector(messagesSelectors.chats)
  const currentChatId = useSelector(messagesSelectors.chatId)
  const currentUserId = useSelector(settingsSelectors.userId)

  useUserChatListSocket(currentUserId ? String(currentUserId) : undefined)

  const sortedChats = useMemo(() => sortChatsByLastMessage(chatsMap), [chatsMap])

  const onClickNewChat = () => {
    dispatch(messagesActions.setChatId(undefined))
    onNavigate?.()
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-4 tw-pt-4 tw-pb-2">
        {setting?.logo?.full && (
          <img
            src={setting?.logo?.thumbnail?.medium ?? setting?.logo?.full}
            alt="Logo"
            className="tw-h-9 tw-w-9 tw-rounded-lg tw-object-cover"
          />
        )}
        <div className="tw-truncate tw-text-base tw-font-bold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </div>
      </div>

      <div className="tw-px-3 tw-pt-2 tw-pb-3">
        <UIButton onClick={onClickNewChat} className="tw-w-full tw-rounded-2xl tw-text-sm">
          <Icon icon={['far', 'plus']} className="tw-h-3.5 tw-w-3.5" />
          <span>{t('openNewChat')}</span>
        </UIButton>
      </div>

      <div className="scrollbar-hidden tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto tw-px-2">
        {isEmpty(sortedChats) ? (
          <ListEmptyComponent
            image={<ChatBubbleSparklesSvg />}
            title={t('emptyChats')}
            description={t('emptyChatsHint')}
          />
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-0.5" onClick={onNavigate}>
            {sortedChats.map((chat) => (
              <ChatRow chat={chat} key={chat._id} selected={chat._id === currentChatId} />
            ))}
          </div>
        )}
      </div>

      <ChatPageSidebarFooter />
    </div>
  )
}
